import type { Chat } from '@liniya/api-client';
import {
  Archive,
  ArchiveRestore,
  Bell,
  BellOff,
  Info,
  MoreVertical,
  Pin,
  PinOff,
} from 'lucide-react';
import { useEffect, useId, useRef, useState } from 'react';

import { useUpdateMembership } from '@liniya/client-core';

const MUTE_HOURS = 8;

interface Props {
  chat: Chat;
  onOpenDetails: () => void;
}

/**
 * The chat's own actions, opened from the header.
 *
 * This used to toggle the details panel, which the button beside it already
 * did — two controls for one thing and none for anything else. The quick
 * preferences live here, as they did before the interface was rewritten; the
 * fuller set, including clearing history and leaving, stays in the details
 * panel, which this menu can open.
 */
export function ChatMenu({ chat, onOpenDetails }: Props) {
  const [open, setOpen] = useState(false);
  const container = useRef<HTMLDivElement>(null);
  const menuId = useId();
  const updateMembership = useUpdateMembership();

  useEffect(() => {
    if (!open) return;
    const onPointerDown = (event: PointerEvent) => {
      if (!container.current?.contains(event.target as Node)) setOpen(false);
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false);
    };
    document.addEventListener('pointerdown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('pointerdown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open]);

  const toggle = (patch: Parameters<typeof updateMembership.mutate>[0]['patch']) => {
    updateMembership.mutate({ chatId: chat.id, patch });
    setOpen(false);
  };

  return (
    <div className="chat-more-menu" ref={container}>
      <button
        className="icon-btn"
        type="button"
        aria-label="Действия с чатом"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-controls={open ? menuId : undefined}
        onClick={() => setOpen((current) => !current)}
      >
        <MoreVertical size={19} />
      </button>

      {open ? (
        <div id={menuId} role="menu">
          <button
            type="button"
            role="menuitem"
            onClick={() => {
              onOpenDetails();
              setOpen(false);
            }}
          >
            <Info size={16} />
            Информация о чате
          </button>

          <button
            type="button"
            role="menuitem"
            disabled={updateMembership.isPending}
            onClick={() => toggle({ is_pinned: !chat.membership.is_pinned })}
          >
            {chat.membership.is_pinned ? <PinOff size={16} /> : <Pin size={16} />}
            {chat.membership.is_pinned ? 'Открепить' : 'Закрепить'}
          </button>

          <button
            type="button"
            role="menuitem"
            disabled={updateMembership.isPending}
            onClick={() =>
              toggle({
                muted_until: chat.membership.is_muted
                  ? null
                  : new Date(Date.now() + MUTE_HOURS * 3600_000).toISOString(),
              })
            }
          >
            {chat.membership.is_muted ? <Bell size={16} /> : <BellOff size={16} />}
            {chat.membership.is_muted
              ? 'Включить уведомления'
              : `Отключить на ${MUTE_HOURS} часов`}
          </button>

          <button
            type="button"
            role="menuitem"
            disabled={updateMembership.isPending}
            onClick={() => toggle({ is_archived: !chat.membership.is_archived })}
          >
            {chat.membership.is_archived ? <ArchiveRestore size={16} /> : <Archive size={16} />}
            {chat.membership.is_archived ? 'Вернуть из архива' : 'Архивировать'}
          </button>
        </div>
      ) : null}
    </div>
  );
}
